import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getRankingByType,
  getPodium,
  getRest,
  entityTypeLabels,
  entityTypeIcons,
  entityTypeDescriptions,
  timeFilterLabels,
  timeFilterIcons,
  trendConfig,
  type RankingEntityType,
  type TimeFilter,
  type DisplayRankingEntry,
} from '@/mocks/rfwScores';

const entityTypes: RankingEntityType[] = ['radios', 'programas', 'locutores', 'clubes', 'fans', 'eventos', 'concursos'];

const timeFilters: TimeFilter[] = ['today', 'week', 'month', 'year', 'all_time'];

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ─── Position Change Badge ─── */
function PositionChange({ current, previous }: { current: number; previous: number }) {
  const delta = previous - current;
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-foreground-400 text-[10px] font-semibold">
        <i className="ri-subtract-line text-[10px]"></i>
        ▬
      </span>
    );
  }
  const isUp = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
      <i className={`${isUp ? 'ri-arrow-up-fill' : 'ri-arrow-down-fill'} text-[10px]`}></i>
      {isUp ? '+' : ''}{delta} posiciones
    </span>
  );
}

/* ─── Trend Badge ─── */
function TrendBadge({ trend, scoreChange }: { trend: string; scoreChange: number }) {
  const cfg = trendConfig[trend as keyof typeof trendConfig];
  if (!cfg) return null;

  const changeLabel = scoreChange > 0 ? `+${scoreChange}` : `${scoreChange}`;
  const changeColor = scoreChange > 0 ? 'text-emerald-600' : scoreChange < 0 ? 'text-red-500' : 'text-foreground-400';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color}`}>
      {cfg.emoji}
      <span className={changeColor}>{changeLabel}</span>
    </span>
  );
}

/* ─── Trophy Badge ─── */
function TrophyBadge({ trophy, position }: { trophy: DisplayRankingEntry['trophy']; position: number }) {
  if (trophy === 'gold') return <span className="text-lg leading-none">🥇</span>;
  if (trophy === 'silver') return <span className="text-lg leading-none">🥈</span>;
  if (trophy === 'bronze') return <span className="text-lg leading-none">🥉</span>;
  if (trophy === 'top10') return (
    <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-[11px] font-extrabold text-amber-700">
      {position}
    </span>
  );
  if (trophy === 'top100') return (
    <span className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-[11px] font-bold text-foreground-500">
      {position}
    </span>
  );
  return (
    <span className="w-6 h-6 rounded-full bg-background-50 flex items-center justify-center text-[11px] font-bold text-foreground-400">
      {position}
    </span>
  );
}

/* ─── PODIUM CARD ─── */
function PodiumCard({ entry, rank, height }: { entry: DisplayRankingEntry; rank: 'gold' | 'silver' | 'bronze'; height: string }) {
  const config = {
    gold: {
      gradient: 'from-amber-400 via-amber-500 to-amber-600',
      badge: 'bg-amber-500',
      icon: 'ri-trophy-fill',
      label: '1º',
      trophy: '🥇',
    },
    silver: {
      gradient: 'from-foreground-300 via-foreground-400 to-foreground-500',
      badge: 'bg-foreground-400',
      icon: 'ri-medal-fill',
      label: '2º',
      trophy: '🥈',
    },
    bronze: {
      gradient: 'from-orange-500 via-orange-600 to-orange-700',
      badge: 'bg-orange-600',
      icon: 'ri-medal-fill',
      label: '3º',
      trophy: '🥉',
    },
  };

  const c = config[rank];

  return (
    <div className="flex flex-col items-center">
      {/* Logo */}
      <div className="relative mb-3">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden bg-background-100 border-2 border-background-200 shadow-lg">
          {entry.logoUrl ? (
            <img src={entry.logoUrl} alt={entry.entityName} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200">
              <i className="ri-radio-line text-2xl text-foreground-300"></i>
            </div>
          )}
        </div>
        <div className={`absolute -top-2 -right-2 w-7 h-7 rounded-full ${c.badge} flex items-center justify-center shadow-md`}>
          <span className="text-background-50 text-sm">{c.trophy}</span>
        </div>
      </div>

      {/* Name */}
      <p className="font-bold text-foreground-950 text-sm text-center leading-tight line-clamp-2 max-w-[120px]">
        {entry.entityName}
      </p>
      <p className="text-foreground-500 text-[11px] mt-0.5 text-center truncate max-w-[120px]">
        {entry.entitySubtitle}
      </p>

      {/* Score */}
      <div className="mt-2">
        <span className="text-sm font-extrabold text-foreground-950">{entry.scoreLabel}</span>
      </div>

      {/* Podium block */}
      <div
        className={`w-full max-w-[110px] bg-gradient-to-b ${c.gradient} rounded-t-xl mt-3 flex flex-col items-center justify-center ${height} shadow-lg`}
      >
        <span className="text-background-50 font-extrabold text-lg leading-none">{c.label}</span>
        <span className="text-background-50/80 text-[10px] font-semibold mt-0.5">RFW Score</span>
      </div>
    </div>
  );
}

/* ─── RANKING ROW ─── */
function RankingRow({ entry }: { entry: DisplayRankingEntry }) {
  return (
    <div className="px-5 py-3 grid grid-cols-[48px_1fr_auto] md:grid-cols-[56px_1fr_140px_100px_100px] gap-3 items-center hover:bg-background-50 transition-colors group">
      {/* Trophy & Position */}
      <div className="flex items-center justify-center">
        <TrophyBadge trophy={entry.trophy} position={entry.position} />
      </div>

      {/* Entity info */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl overflow-hidden bg-background-100 shrink-0 border border-background-100">
          {entry.logoUrl ? (
            <img src={entry.logoUrl} alt={entry.entityName} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200">
              <i className="ri-radio-line text-sm text-foreground-300"></i>
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground-950 truncate">{entry.entityName}</p>
          <p className="text-[11px] text-foreground-400 truncate">{entry.entitySubtitle}</p>
        </div>
      </div>

      {/* Score + Change */}
      <div className="hidden md:flex flex-col items-end gap-0.5">
        <span className="text-sm font-extrabold text-foreground-950">{entry.scoreLabel}</span>
        <PositionChange current={entry.position} previous={entry.previousPosition} />
      </div>

      {/* Followers / Highlight */}
      <div className="hidden md:flex flex-col items-end gap-0.5">
        {entry.followers != null ? (
          <>
            <span className="text-sm font-semibold text-foreground-700">{formatNumber(entry.followers)}</span>
            <span className="text-[10px] text-foreground-400">Seguidores</span>
          </>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-[10px] font-semibold text-foreground-500">
            {entry.highlight}
          </span>
        )}
      </div>

      {/* Trend */}
      <div className="hidden md:flex justify-end">
        <TrendBadge trend={entry.trend} scoreChange={entry.scoreChange} />
      </div>

      {/* Mobile score */}
      <div className="md:hidden flex flex-col items-end gap-0.5">
        <span className="text-sm font-extrabold text-foreground-950">{entry.scoreLabel}</span>
        <TrendBadge trend={entry.trend} scoreChange={entry.scoreChange} />
      </div>
    </div>
  );
}

export default function RankingsPage() {
  const [activeEntity, setActiveEntity] = useState<RankingEntityType>('radios');
  const [activeTimeFilter, setActiveTimeFilter] = useState<TimeFilter>('week');

  const ranking = useMemo(
    () => getRankingByType(activeEntity, activeTimeFilter),
    [activeEntity, activeTimeFilter],
  );

  const podium = useMemo(() => getPodium(ranking), [ranking]);
  const rest = useMemo(() => getRest(ranking), [ranking]);

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20podium%20scene%20with%20dramatic%20lighting%2C%20elegant%20award%20stage%20with%20three%20pedestals%20gold%20silver%20and%20bronze%20tones%2C%20warm%20spotlight%20beams%20from%20ceiling%2C%20dark%20atmospheric%20background%20with%20subtle%20sparkle%20particles%2C%20prestigious%20competition%20ceremony%20aesthetic%2C%20cinematic%20composition%20with%20depth%20of%20field%2C%20luxury%20awards%20night%20atmosphere&width=1600&height=900&seq=rankings-hero-v2&orientation=landscape"
          alt="Rankings RFW Score"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-flashlight-fill text-3xl text-amber-400"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Rankings RFW Score
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            La competencia es visible. Quién sube, quién baja, quién domina. Basado en COMUNIDAD, no solo en audiencia.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
              {entityTypes.length} categorías
            </span>
            <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
              Actualizado diariamente
            </span>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* ENTITY TYPE TABS */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
              <i className="ri-bar-chart-2-line text-base text-primary-600"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950">Rankings por Categoría</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {entityTypes.map((et) => {
              const isActive = et === activeEntity;
              return (
                <button
                  key={et}
                  onClick={() => setActiveEntity(et)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-500 text-background-50 shadow-lg'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${entityTypeIcons[et]} text-sm`}></i>
                  {entityTypeLabels[et]}
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-[12px] text-foreground-400 flex items-center gap-1.5">
            <i className="ri-information-line text-xs"></i>
            {entityTypeDescriptions[activeEntity]}
          </p>
        </section>

        {/* TIME FILTERS */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center">
              <i className="ri-time-line text-base text-secondary-600"></i>
            </div>
            <h2 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Período</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {timeFilters.map((tf) => {
              const isActive = tf === activeTimeFilter;
              return (
                <button
                  key={tf}
                  onClick={() => setActiveTimeFilter(tf)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-secondary-500 text-background-50 dark:text-foreground-950 shadow-lg'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${timeFilterIcons[tf]} text-sm`}></i>
                  {timeFilterLabels[tf]}
                </button>
              );
            })}
          </div>
        </section>

        {/* PODIUM — TOP 3 */}
        <section>
          <div className="bg-background-100 rounded-3xl p-6 md:p-10">
            <h3 className="text-center text-sm font-semibold text-foreground-400 uppercase tracking-wider mb-8">
              Top 3 — {entityTypeLabels[activeEntity]} · {timeFilterLabels[activeTimeFilter]}
            </h3>

            <div className="flex items-end justify-center gap-4 md:gap-6">
              {podium.silver && <PodiumCard entry={podium.silver} rank="silver" height="h-28 md:h-36" />}
              {podium.gold && <PodiumCard entry={podium.gold} rank="gold" height="h-36 md:h-44" />}
              {podium.bronze && <PodiumCard entry={podium.bronze} rank="bronze" height="h-24 md:h-28" />}
            </div>
          </div>
        </section>

        {/* FULL RANKING LIST */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">
                Ranking Completo
              </h3>
              <span className="px-2 py-0.5 rounded-full bg-background-100 text-[10px] font-bold text-foreground-400">
                {ranking.length} {entityTypeLabels[activeEntity].toLowerCase()}
              </span>
            </div>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-3 grid grid-cols-[48px_1fr_auto] md:grid-cols-[56px_1fr_140px_100px_100px] gap-3 items-center bg-background-100 border-b border-background-200">
              <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider text-center">#</span>
              <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">
                {entityTypeLabels[activeEntity].slice(0, -1)}
              </span>
              <span className="hidden md:block text-[10px] font-bold text-foreground-400 uppercase tracking-wider text-right">
                {activeTimeFilter === 'today' ? 'Score Hoy' : activeTimeFilter === 'week' ? 'Score Semanal' : activeTimeFilter === 'month' ? 'Score Mensual' : activeTimeFilter === 'year' ? 'Score Anual' : 'Score Histórico'}
              </span>
              <span className="hidden md:block text-[10px] font-bold text-foreground-400 uppercase tracking-wider text-right">
                {activeEntity === 'fans' || activeEntity === 'eventos' || activeEntity === 'concursos' ? 'Destacado' : 'Seguidores'}
              </span>
              <span className="hidden md:block text-[10px] font-bold text-foreground-400 uppercase tracking-wider text-right">
                Tend.
              </span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-background-100">
              {rest.length > 0 ? (
                rest.map((entry) => <RankingRow key={`${entry.entityId}-${entry.position}`} entry={entry} />)
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-list-check text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-400">No hay más entradas</p>
                  <p className="text-[12px] text-foreground-300 mt-1">El Top 3 muestra a los líderes de esta categoría</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* TROPHY LEGEND */}
        <section className="bg-background-100 rounded-3xl p-6 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
              <i className="ri-award-line text-base text-accent-600"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950">Sistema de Trofeos</h2>
          </div>

          <p className="text-sm text-foreground-500 leading-relaxed mb-6">
            Los trofeos se asignan automáticamente según la posición en el ranking RFW Score. No se pueden comprar ni editar manualmente.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { trophy: '🥇', label: 'Oro', desc: '1er lugar', cls: 'bg-amber-50 border-amber-200' },
              { trophy: '🥈', label: 'Plata', desc: '2do lugar', cls: 'bg-stone-50 border-stone-200' },
              { trophy: '🥉', label: 'Bronce', desc: '3er lugar', cls: 'bg-orange-50 border-orange-200' },
              { trophy: '⭐', label: 'Top 10', desc: 'Posiciones 4-10', cls: 'bg-amber-50/50 border-amber-100' },
              { trophy: '🔹', label: 'Top 100', desc: 'Posiciones 11-100', cls: 'bg-background-50 border-background-200' },
            ].map((t, i) => (
              <div key={i} className={`rounded-2xl p-4 border ${t.cls} text-center`}>
                <span className="text-2xl">{t.trophy}</span>
                <p className="text-sm font-extrabold text-foreground-950 mt-2">{t.label}</p>
                <p className="text-[11px] text-foreground-400">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CRITERIA EXPLANATION */}
        <section className="bg-background-100 rounded-3xl p-6 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center">
              <i className="ri-scales-line text-base text-secondary-600"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950">¿Cómo se Calcula el RFW Score?</h2>
          </div>

          <p className="text-sm text-foreground-500 leading-relaxed mb-6">
            RFW Score no premia solamente la audiencia. Premia la <strong className="text-foreground-700">COMUNIDAD</strong>. Una radio con menos oyentes pero más interacción real puede superar a una radio grande con audiencia pasiva. El sistema evalúa decenas de acciones: publicaciones, eventos, concursos, comentarios, likes, compartidos, nuevos seguidores, actividad en cabina, saludos, canciones, encuestas, participación en concursos, invitados registrados y más.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: 'ri-flashlight-fill',
                title: 'Radio Activa',
                desc: 'Radio encendida (+1/hora), publicaciones (+5), eventos (+15), concursos (+25), programas activos (+10), locutores activos (+10).',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Comunidad Participativa',
                desc: 'Comentarios (+1), likes (+1), compartidos (+5), nuevos seguidores (+10), miembros del club (+15).',
              },
              {
                icon: 'ri-broadcast-line',
                title: 'Cabina Interactiva',
                desc: 'Saludos recibidos (+2), canciones solicitadas (+2), encuestas respondidas (+2), concursos (+5), ganadores (+10).',
              },
              {
                icon: 'ri-user-star-line',
                title: 'Oyente Comprometido',
                desc: 'Perfil creado (+20), primer saludo (+10), primera canción (+10), seguir radios (+5), compartir (+10), invitar amigos (+25).',
              },
              {
                icon: 'ri-error-warning-line',
                title: 'Penalizaciones',
                desc: 'Sin actividad 7 días (-50), sin publicaciones 14 días (-100), sin programas activos (-50), sin interacción real (-100), spam (-100).',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Reglas Claras',
                desc: 'Prohibido comprar Score, editar manualmente o modificar rankings. Todo es automático y transparente.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-background-200 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-sm text-foreground-600`}></i>
                </div>
                <h4 className="text-sm font-extrabold text-foreground-950 mb-1.5">{item.title}</h4>
                <p className="text-[12px] text-foreground-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* STATS BAR */}
        <section className="bg-background-100 rounded-3xl p-6 flex flex-wrap gap-6 items-center justify-center">
          {[
            { icon: 'ri-trophy-line', label: 'Categorías', value: entityTypes.length.toString() },
            { icon: 'ri-radio-line', label: `${entityTypeLabels[activeEntity]} en Ranking`, value: ranking.length.toString() },
            { icon: 'ri-flashlight-fill', label: 'Basado en', value: 'RFW Score' },
            { icon: 'ri-refresh-line', label: 'Próxima actualización', value: '24 horas' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}